import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { apiCache, dashboardCache, layoutCache } from '@processo/shared/components/entity-layout/smart-table/cache';
import { EntityDashboardServiceService } from '@processo/shared/service/entity-dashboard-service.service';
import { StateService } from '@processo/shared/state/state.service';
import { format, startOfMonth, endOfMonth, addDays, subDays, startOfDay, endOfDay } from 'date-fns';
import { environment } from 'projects/processo/src/environments/environment';
import { BehaviorSubject, map, of, Subject, tap } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';

@Injectable(
  {providedIn: 'root'}
)
export class OrgDashboardService {
  private _dashboardConfig = signal({
    tabChange: false,
    resize: false,
    positionChange: false,
    removeWidget: false,
    previewMode:false
  });

  private _widgetsRefreshed = signal<string[]>([]);
  widgetsRefreshed = this._widgetsRefreshed.asReadonly();
  setWidgetsRefreshed(widgetId: string) {
    this._widgetsRefreshed.update(prev => [...prev, widgetId]);
  }
  clearWidgetsRefreshed(widgetId: string) { 
    this._widgetsRefreshed.update(prev => prev.filter(id => id !== widgetId));
  }
  resetWidgetsRefreshed() {
    this._widgetsRefreshed.set([]);
  }
  private filterStateMap = new Map<string, any[]>();
  private lastEntityKey: string | null = null;
  //signal to know if we are in a dashboard view.
  private isInDashboardContext = signal(false);
  // Store pending entity filters that need to be applied when dashboard loads
  private pendingEntityFilters = new Map<string, any[]>();

  public drilldownKey: string | null = null;

  /** Tab where an erugrid dashboard-to-tab drill started; used to restore filters on return. */
  private drilldownSourceTabId: string | null = null;
  private drilldownTargetTabId: string | null = null;
  private preDrilldownFilterSnapshot: any[] | null = null;
  /** org_process_did (or rd id) at drill time — survives resetState(null). */
  private drilldownContextDashboardKey: string | null = null;

  //Public method for components to update the context.
  setDashboardContext(inContext: boolean) {
    this.isInDashboardContext.set(inContext);
  }

  storeFilterState(key: string, filters: any[]) {
    this.filterStateMap.set(key, filters);
    this._filterState.set(filters);
    // Keep dashboard state in sync so the filter UI and dependent dropdown lookups
    // always see the latest values (avoids stale parent values -> missing/incorrect dfilter).
    try {
      this.currentDashBoard.update((prev: any) => {
        if (!prev?.dashboard) return prev;
        return {
          ...prev,
          dashboard: {
            ...prev.dashboard,
            filters: Array.isArray(filters) ? filters : prev.dashboard.filters,
          },
        };
      });
    } catch {}
    try {
      // sessionStorage.setItem(`dbf::${key}`, JSON.stringify(filters));
      const current = this.currentDashBoard();
      const meta = { org_id: current?.org_id, process_id: current?.process_id, org_process_did: current?.org_process_did };
      // sessionStorage.setItem(`dbf::last`, JSON.stringify({ key, meta, filters }));
    } catch {}
  }
  
  getStoredFilterState(key: string): any[] {
    const inMemory = this.filterStateMap.get(key) || [];
    if (inMemory.length > 0) return inMemory;
    try {
      const raw = sessionStorage.getItem(`dbf::${key}`);
      if (raw) return JSON.parse(raw);
      
    } catch {}
    return [];
  }

  getConfig = computed(() => this._dashboardConfig());
  private _filterState = signal<any[]>([]);
  filterState = this._filterState.asReadonly();

  updateFilterState(filters: any[]) {
    this._filterState.set(filters);
    // When dashboard filters are actively updated, un-suppress entity filters
    try {
      const hasAny = Array.isArray(filters) && filters.some((f: any) => {
        const v = f?.default;
        return !(v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0));
      });
      if (hasAny) {
        layoutCache.set('filtersSuppressed', false);
      }
    } catch {}
  }


  
  getFilterState(): any[] {
    return this._filterState();
  }
  
  checkAndClearFiltersOnEntityChange(currentEntityKey: string) {
    const currentEntityPart = this.extractEntityPart(currentEntityKey);
    const lastEntityPart = this.lastEntityKey ? this.extractEntityPart(this.lastEntityKey) : null;
    
    if (lastEntityPart && currentEntityPart !== lastEntityPart && !this.drilldownKey) {
      //console.log('Entity changed - clearing all filter states');
      this.filterStateMap.clear();
      this._filterState.set([]);
      // NOTE: We intentionally do NOT clear pendingEntityFilters here
      // as they should persist across entity changes for bidirectional sync
    }
    
    this.lastEntityKey = currentEntityKey;
  }
  
  private extractEntityPart(dashboardKey: string): string {
    const parts = dashboardKey.split('_');
    if (parts.length >= 2) {
      return `${parts[0]}_${parts[1]}`; // org_id_process_id
    }
    return dashboardKey;
  }
  
  clearInMemoryFilterState(key: string) {
    //console.log(`Clearing in-memory filter state for key: ${key}`);
    this.filterStateMap.delete(key);
  }
  

  setConfig(config: Partial<{
    tabChange: boolean, 
    resize: boolean, 
    positionChange: boolean, 
    removeWidget: boolean,
    previewMode:boolean
  }>) {
    this._dashboardConfig.update(currentConfig => ({
      ...currentConfig,
      ...config
    }));
  }

  resetConfig() {
    this._dashboardConfig.set({
      tabChange: false,
      resize: false,
      positionChange: false,
      removeWidget: false,
      previewMode:true
    });
  }
  
  private stateService = inject(StateService);
  /** Cache for get_apis response keyed by `${org_id}_${process_id}`. One-time fetch per dashboard context. */
  private apisCache = new Map<string, any>();

  constructor(private http: HttpClient) { }
  currentDashBoard = signal<any>(null)
  selectedTab = signal(1)
  getCurrentTab=computed(()=>this.selectedTab()-1)
  openWidgetSlider = false;
  openWidgetfilter = false;
  //isDraggingFromWidgetPanel = false;
  drilldownExtraFilters = signal<Record<string, any>>({});
  update_query = new BehaviorSubject<any>(null);
  private inFlightRequests = new Map<string, any>();

  /** Cache key for current dashboard (org_id + process_id). */
  private getApisCacheKey(): string | null {
    const current = this.currentDashBoard();
    const org_id = current?.org_id;
    const process_id = current?.process_id;
    if (!org_id || !process_id) return null;
    return `${org_id}_${process_id}`;
  }

  /** Returns cached get_apis response for current dashboard, or null. No network call. */
  getCachedApis(): any[] | null {
    const key = this.getApisCacheKey();
    if (!key || !this.apisCache.has(key)) return null;
    const res = this.apisCache.get(key);
    return Array.isArray(res) ? res : null;
  }

  /** Fetches get_apis once per org_id+process_id and caches. Use for preload or single lazy load. */
  getApisForCurrentDashboard() {
    const key = this.getApisCacheKey();
    if (!key) return of(null);
    if (this.apisCache.has(key)) return of(this.apisCache.get(key));
    return this.stateService.getApis({ org_id: this.currentDashBoard()?.org_id, process_id: this.currentDashBoard()?.process_id }).pipe(
      tap((res: any) => { if (res != null) this.apisCache.set(key, res); }),
      shareReplay(1)
    );
  }

  /** Call when dashboard is set to preload get_apis so custom actions use cache only. */
  preloadApisForCurrentDashboard(): void {
    const key = this.getApisCacheKey();
    if (!key || this.apisCache.has(key)) return;
    this.getApisForCurrentDashboard().subscribe();
  }

  getQueryFetch(body: any, options?: { skipCache?: boolean }) {
    const stringifyBody = JSON.stringify(body);
    const skipCache = options?.skipCache === true;

    if (!skipCache) {
      if (dashboardCache.has(stringifyBody)) {
        return of(dashboardCache.get(stringifyBody));
      }

      if (this.inFlightRequests.has(stringifyBody)) {
        return this.inFlightRequests.get(stringifyBody);
      }
    }

    const request$ = this.http
      .post(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/query_fetch`, body)
      .pipe(
        tap((response) => {
          if (!skipCache) {
            dashboardCache.set(stringifyBody, response);
            this.update_query.next(stringifyBody);
          }
        }),
        finalize(() => {
          if (!skipCache) {
            this.inFlightRequests.delete(stringifyBody);
          }
        }),
        shareReplay(1)
      );

    if (!skipCache) {
      this.inFlightRequests.set(stringifyBody, request$);
    }
    return request$;
  }

  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

   sortTabs<T extends { tab_idx: number }>(tabs: T[]): T[] {
    return tabs.sort((a, b) => a.tab_idx - b.tab_idx);
  }

  fetchDashboardList(body: {
    "org_id": string,
    "process_id": string
  }) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/fetch_dashboard_list`, body);
  }
  thousandsFormatter(value:number) {
    if (value >= 1_000_00) return (value / 1_000_00).toFixed(1) + ' L';
    if (value >= 1_000) return (value / 1_000).toFixed(1) + ' K';
    return value;
  }
  applyCustomFormatters(option:any) {
    if (option.yAxis?.axisLabel?.useThousandsFormatter) {
      option.yAxis.axisLabel.formatter = (value: number) => this.thousandsFormatter(value);
    }
  
    if (option.series) {
      const seriesArray = Array.isArray(option.series) ? option.series : [option.series];
      seriesArray.forEach((series: any, idx: number) => {
        if (series?.label?.useThousandsFormatter) {
          series.label.formatter = (params: any) => {
            const value = Array.isArray(params?.value)
              ? params.value[1]
              : (typeof params?.value === 'object' && params?.value !== null && 'y' in params.value)
                ? (params.value as any).y
                : params?.value;
            return this.thousandsFormatter(Number(value));
          };
        }
      });
      option.series = seriesArray;
    }
    // Repeat for other axes or series if needed
    return option;
  }
  dashboard_fetch(body: { did: string }) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/dashboard_fetch`, body).pipe(
      map(r => ({
        ...r,
        dashboard: {
          ...r.dashboard,
          tabs: this.sortTabs(r.dashboard.tabs),
          filters: this.filterSchema(r.dashboard.filters),
        },
      })),


    );
  }
  
  async update_default_filter(data:any,data_id:any){
    const filter_data = await Promise.all(
      data?.map(async (_filter: any) => {
        if (_filter.default_first_value && (_filter.type === 'dropdown_single_select' || _filter.type === 'dropdown_multi_select')) {
          // If user (or drilldown override / stored state) already provided a value,
          // do NOT overwrite it by fetching "first value" again.
          const hasExistingValue =
            (_filter.type === 'dropdown_multi_select' && Array.isArray(_filter.default) && _filter.default.length > 0) ||
            (_filter.type !== 'dropdown_multi_select' && _filter.default !== null && _filter.default !== undefined && _filter.default !== '');
          if (hasExistingValue) {
            return _filter;
          }
          
          // ✅ NEW: Check if this filter depends on another filter
          if (_filter.df) {
            const parentFilter = data.find((f: any) => f.name === _filter.df);
            if (!parentFilter?.default || 
                parentFilter.default === '' || 
                parentFilter.default === null ||
                (Array.isArray(parentFilter.default) && parentFilter.default.length === 0)) {
              //console.log(`⏭️ Skipping ${_filter.name} in update_default_filter because parent ${_filter.df} is empty`);
              return _filter; // Return filter without fetching default value
            }
          }
          
          _filter.default = await this.fetch_dashboard_data(_filter, data_id, data);
        }
        return _filter;
      })
    );
    return filter_data
  }
  
  dashboart_service = inject(EntityDashboardServiceService);
  fetch_dashboard_data(filter: any, data_id: any, allFilters: any[] = []): Promise<any> {
    return new Promise((resolve) => {
      const body: any = {
        query: filter.query,
        transform: filter.transform,
        field_str: '',
        org_id: data_id?.org_id,
        process_id: data_id?.process_id,
      };

      // If this filter depends on a parent (df), send dfilter so API can return
      // the correct dependent option list / first value.
      if (filter?.df) {
        const parent = (allFilters || []).find((f: any) => f?.name === filter.df);
        const parentValue = parent?.default;
        let parentHasMeaningfulValue = false;

        if (Array.isArray(parentValue)) {
          if (parentValue.length > 0) {
            body.dfilter = parentValue;
            parentHasMeaningfulValue = true;
          }
        } else if (typeof parentValue === 'string' && parentValue.length > 0) {
          body.dfilter = [parentValue];
          parentHasMeaningfulValue = true;
        } else if (typeof parentValue === 'boolean' || typeof parentValue === 'number') {
          body.dfilter = [parentValue];
          parentHasMeaningfulValue = true;
        }

        if (!parentHasMeaningfulValue) {
          body.dfilter = [''];
        }
      }
  
      this.dashboart_service.fetch_dashboard_data(body).subscribe({
        next: (res: any) => {
          let optionList = Array.isArray(res) ? res : [res];
          resolve(optionList[0][filter.name]);
        },
        error: () => {
          resolve(filter?.default);
        },
      });
    });
  }
  
  saveDashboardNew(body: { 
    "org_id": string, 
    "process_id": string 
  }) {
    //console.log('body',body);
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/save_dashboard`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }
  
  saveDashboardEditFilter(body:any) {
    //console.log('body',body);
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/save_dashboard`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  saveDashboardEdit(body: any) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/save_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  removeDashboardWidget(body: any) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/remove_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  saveDashboardWidget(body: any) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/save_dashboard_widget`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  removeDashboard(body: { "org_id": string, "process_id": string, "org_process_did": string }) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/remove_dashboard`, body);
  }

  saveDashboardTab(body: { "org_process_did": string, "tabs": Array<{ "tab_id": string, "tab_idx": number, "tab_name": string }> }) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/save_dashboard_tab`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs) } }))
    );
  }

  removeDashboardTab(body: { "org_process_did": string, "tab_id": string }) {
    return this.http.post<any>(`${environment.base_eru_func_url}/processo/${this.stateService.getOrgProcessId(body)}/func/remove_dashboard_tab`, body).pipe(
      map(r => ({ ...r, dashboard: { ...r.dashboard, tabs: this.sortTabs(r.dashboard.tabs), filters:this.filterSchema(r.dashboard.filters) } }))
    );
  }

  async resetState() {
    let currentDashboardId = sessionStorage.getItem('current_dashboard_id');
    
    // Preserve pending entity filters before resetting
    const preservedPendingFilters = new Map(this.pendingEntityFilters);
    const preservedDrillContextDashboardKey = this.drilldownContextDashboardKey;
    //console.log('[OrgDashboardService] Preserving pending filters during resetState:', Array.from(preservedPendingFilters.keys()));
    
    this.currentDashBoard.set(null);
    this.selectedTab.set(1);
    this.filterStateMap.clear();
    this.apisCache.clear();
    this.lastEntityKey = null;
    this.openWidgetSlider = false;
    this.drilldownKey = null;
    this.drilldownSourceTabId = null;
    this.drilldownTargetTabId = null;
    this.preDrilldownFilterSnapshot = null;
    this.drilldownExtraFilters.set({});
    dashboardCache.clear();
    this.resetConfig();  
    this.updateFilterState([]);
    
    // Restore pending entity filters after reset
    this.pendingEntityFilters = preservedPendingFilters;
    //console.log('[OrgDashboardService] Restored pending filters after resetState:', Array.from(this.pendingEntityFilters.keys()));

    this.drilldownContextDashboardKey = preservedDrillContextDashboardKey;
    this.hydrateDrilldownTabContextFromStorage();
    
    this.setDashboardContext(false);
    try {
      // Clear dashboard-related caches when resetting dashboard
      const { dashboard_filter, dashboardCache } = await import('@processo/shared/components/entity-layout/smart-table/cache');
      dashboard_filter.clear();
      dashboardCache.clear();
    } catch {}
  }

  getLastSavedFiltersFromStorage(): { meta: any, filters: any[] } | null {
    try {
      const raw = sessionStorage.getItem('dbf::last');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return { meta: parsed?.meta || null, filters: parsed?.filters || [] };
    } catch {
      return null;
    }
  }

  filterSchema(filters:any){
    // Helper to parse tokens like CURRENT_DATE +/- N, FIRST_DAY, LAST_DAY
    const parseRelativeTokenToDate = (expr: any): Date | null => {
      if (!expr || typeof expr !== 'string') return null;
      const s = expr.trim();
      if (/^current_date$/i.test(s)) {
        return new Date();
      }
      if (/^first_day$/i.test(s)) {
        return startOfMonth(new Date());
      }
      if (/^last_day$/i.test(s)) {
        return endOfMonth(new Date());
      }
      const m = s.match(/current_date\s*([+-])\s*(\d+)/i);
      if (m) {
        const op = m[1];
        const days = parseInt(m[2], 10);
        if (!isNaN(days) && days >= 0) {
          return op === '-' ? subDays(new Date(), days) : addDays(new Date(), days);
        }
      }
      return null;
    };

    const mappedFilters = filters?.map((_filter: any) => {
      if (_filter.type === 'date_range') {
        // Convert common placeholders
        if (Array.isArray(_filter.default)) {
          // e.g., ["CURRENT_DATE - 1", "CURRENT_DATE - 1"] or tokens
          if (_filter.default.length === 2) {
            const startParsed = parseRelativeTokenToDate(_filter.default[0]);
            const endParsed = parseRelativeTokenToDate(_filter.default[1]);
            if (startParsed && endParsed) {
              return { ..._filter, default: [startParsed, endParsed] };
            }
          }
          // FIRST_DAY/LAST_DAY array
          if (_filter.default.includes('FIRST_DAY') && _filter.default.includes('LAST_DAY')) {
            return {
              ..._filter,
              default: [
                startOfMonth(new Date()),
                endOfMonth(new Date()),
              ],
            };
          }
        } else if (typeof _filter.default === 'string') {
          // Single string range expressions or single-day placeholders
          const s = _filter.default;
          if (/^current_date$/i.test(s)) {
            return {
              ..._filter,
              default: [startOfDay(new Date()), endOfDay(new Date())],
            };
          }
          if (s.includes('FIRST_DAY') && s.includes('LAST_DAY')) {
            return {
              ..._filter,
              default: [
                startOfMonth(new Date()),
                endOfMonth(new Date()),
              ],
            };
          }
        }
        if (_filter.default == null) {
          return { ..._filter, default: [] };
        }
      } else if (_filter.type === 'date') {
        if (_filter.default == null) {
          return { ..._filter, default: '' };
        }
        const parsed = parseRelativeTokenToDate(_filter.default);
        if (parsed) {
          return { ..._filter, default: parsed };
        }
        if (_filter.default === 'Current_Date') {
          return { ..._filter, default: new Date() };
        }
      } else if (_filter.type === 'dropdown_multi_select' && _filter.default === '') {
        return { ..._filter, default: [] };
      }
      return _filter; // Return other filters as is
    });
    return mappedFilters;
  }
  
  
  getFilterJsonForApiCall() {
    if (!this.isInDashboardContext()) {
      //console.log('[getFilterJsonForApiCall] not in dashboard context, returning {}');
      return {};
    }
  
    let data: any = {};
    const currentDashboard = this.currentDashBoard();
    if (!currentDashboard) {
      //console.log('[getFilterJsonForApiCall] no currentDashboard, returning {}');
      return {};
    }
  
    const dashboardKey = `${currentDashboard?.org_id}_${currentDashboard?.process_id}_${currentDashboard?.org_process_did}`;
    const storedFilters = this.getStoredFilterState(dashboardKey);
    const filtersToUse = storedFilters.length > 0 ? storedFilters : this.filterSchema(currentDashboard?.dashboard?.filters);
    //console.log('[getFilterJsonForApiCall] dashboardKey:', dashboardKey, 'filtersToUse:', filtersToUse?.map((f: any) => ({ name: f.name, type: f.type, default: f.default })));
  
    filtersToUse?.forEach((_field: any) => {
      // ✅ Check if this field depends on another field (has 'df' property)
      // if (_field.df) {
      //   const parentFilter = filtersToUse.find((f: any) => f.name === _field.df);
      //   if (parentFilter && this.isEmptyValue(parentFilter.default)) {
      //     //console.log(`⏭️ Skipping ${_field.name} because parent ${_field.df} is empty`);
      //     return; // Skip this dependent filter
      //   }
      // }
  
      // ✅ Only include filters with meaningful values
      if (_field && this.hasValidValue(_field)) {
        if (_field.type === 'date') {
          let format_date = format(new Date(_field.default), _field.format || 'yyyy-MM-dd');
          data[`${_field.name}`] = format_date;
        } else if (_field.type === 'date_range') {
          if (Array.isArray(_field.default) && _field.default.length === 2) {
            let start_date = format(new Date(_field.default[0]), _field.format || 'yyyy-MM-dd');
            let end_date = format(new Date(_field.default[1]), _field.format || 'yyyy-MM-dd');
            data[`from_${_field.name}`] = start_date;
            data[`to_${_field.name}`] = end_date;
          }
        } else if (_field.type === 'dropdown_multi_select') {
          if (Array.isArray(_field.default) && _field.default.length > 0) {
            //console.log(`[DashboardService] Creating filter for ${_field.name}:`, _field.default);
            data[`${_field.name}`] = { $in: _field.default };
           // console.log(`[DashboardService] Result filter:`, data[`${_field.name}`]);
          }
        } else if (_field?.type === 'checkbox') {
          if (_field.default === true || _field.default === false) {
            data[`${_field.name}`] = _field.default;
          }
        } else {
          data[`${_field.name}`] = _field.default;
        }
      }
    });
    
    const extra = this.drilldownExtraFilters();
    if (extra) {
      Object.assign(data, extra);
    }
    //console.log('[getFilterJsonForApiCall] result:', data);
    return data;
  }

  /**
   * Returns params object with ALL dashboard filter keys.
   * Keys with selected values use that value; others use default/empty (for exec_api payload requirement).
   */
  getFilterParamsWithAllKeys(): Record<string, any> {
    const currentDashboard = this.currentDashBoard();
    if (!currentDashboard?.dashboard?.filters || !this.isInDashboardContext()) {
      return {};
    }
    const dashboardKey = `${currentDashboard?.org_id}_${currentDashboard?.process_id}_${currentDashboard?.org_process_did}`;
    const storedFilters = this.getStoredFilterState(dashboardKey);
    const filtersToUse = storedFilters.length > 0 ? storedFilters : this.filterSchema(currentDashboard.dashboard.filters);
    const values = this.getFilterJsonForApiCall();
    const params: Record<string, any> = {};
    filtersToUse?.forEach((_field: any) => {
      if (!_field?.name) return;
      if (_field.type === 'date_range') {
        params[`from_${_field.name}`] = values[`from_${_field.name}`] ?? '';
        params[`to_${_field.name}`] = values[`to_${_field.name}`] ?? '';
      } else {
        params[_field.name] = values[_field.name] ?? _field.default ?? '';
      }
    });
    return params;
  };
  
  // ✅ Helper method to check if value is truly empty
  private isEmptyValue(value: any): boolean {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    if (Array.isArray(value) && value.length === 0) {
      return true;
    }
    return false;
  }
  
  // ✅ Helper method to check if field has valid value
  private hasValidValue(field: any): boolean {
    if (this.isEmptyValue(field.default)) {
      return false;
    }
    
    // ✅ For date objects, check if they're valid dates
    if (field.type === 'date' && field.default instanceof Date) {
      return !isNaN(field.default.getTime());
    }
    
    // ✅ For date range, check if it's an array with 2 valid dates
    if (field.type === 'date_range' && Array.isArray(field.default)) {
      return field.default.length === 2 && 
             field.default.every((date: any) => date instanceof Date && !isNaN(date.getTime()));
    }
    
    return true;
  }
  
  setDrilldownContext(sourceKey: string) {
    this.drilldownKey = sourceKey;
  }

  private getDashboardFilterKey(): string | null {
    const d = this.currentDashBoard();
    if (!d?.org_id || !d?.process_id || !d?.org_process_did) return null;
    return `${d.org_id}_${d.process_id}_${d.org_process_did}`;
  }

  private drilldownTabContextStorageKey(dashboardKey?: string | null): string | null {
    const id =
      dashboardKey ??
      this.drilldownContextDashboardKey ??
      this.currentDashBoard()?.org_process_did ??
      (this.currentDashBoard() as any)?.id;
    return id ? `drilldown_tab_context::${id}` : null;
  }

  private persistDrilldownTabContextToStorage(): void {
    const storageKey = this.drilldownTabContextStorageKey();
    if (!storageKey || !this.drilldownSourceTabId) return;
    if (!this.preDrilldownFilterSnapshot?.length) return;
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          sourceTabId: this.drilldownSourceTabId,
          targetTabId: this.drilldownTargetTabId,
          baselineFilters: this.preDrilldownFilterSnapshot,
        })
      );
    } catch {}
  }

  private hydrateDrilldownTabContextFromStorage(): void {
    const storageKey = this.drilldownTabContextStorageKey();
    if (!storageKey) return;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return;
      const ctx = JSON.parse(raw);
      this.drilldownSourceTabId = ctx?.sourceTabId ?? null;
      this.drilldownTargetTabId = ctx?.targetTabId ?? null;
      this.preDrilldownFilterSnapshot = Array.isArray(ctx?.baselineFilters)
        ? ctx.baselineFilters
        : Array.isArray(ctx?.filters)
          ? ctx.filters
          : null;
    } catch {}
  }

  clearDrilldownTabContext(): void {
    const storageKey = this.drilldownTabContextStorageKey();
    if (storageKey) {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {}
    }
    this.drilldownContextDashboardKey = null;
    this.drilldownSourceTabId = null;
    this.drilldownTargetTabId = null;
    this.preDrilldownFilterSnapshot = null;
  }

  /** Snapshot filter bar state before a dashboard/tab drill so the source tab can be restored. */
  private captureDrilldownTabContext(
    sourceFilters: any[],
    targetTabId?: string
  ): void {
    const dash = this.currentDashBoard();
    this.drilldownContextDashboardKey =
      dash?.org_process_did ?? (dash as any)?.id ?? null;

    const tabs = dash?.dashboard?.tabs || [];
    const sourceTab = tabs[this.getCurrentTab()];
    this.drilldownSourceTabId = sourceTab?.tab_id ?? null;
    this.drilldownTargetTabId = targetTabId ?? null;
    this.preDrilldownFilterSnapshot =
      Array.isArray(sourceFilters) && sourceFilters.length > 0
        ? JSON.parse(JSON.stringify(sourceFilters))
        : null;

    if (this.preDrilldownFilterSnapshot?.length) {
      this.persistDrilldownTabContextToStorage();
    }
  }

  /**
   * Manual tab clicks always use pre-drill dashboard filters.
   * Drill filters apply only when arriving via performDashboardDrilldown (load_dashboard).
   */
  onManualTabChange(_tabId: string | undefined): void {
    this.hydrateDrilldownTabContextFromStorage();
    if (!this.drilldownSourceTabId) {
      return;
    }

    if (this.preDrilldownFilterSnapshot?.length) {
      this.applyBaselineFilters();
    } else {
      this.drilldownExtraFilters.set({});
      dashboardCache.clear();
      this.refreshWidgets.next();
    }
  }

  private applyFilterState(
    filters: any[],
    extraFilters: Record<string, any>
  ): void {
    const key = this.getDashboardFilterKey();
    if (key) {
      this.storeFilterState(key, filters);
    }
    this.updateFilterState(filters);
    this.drilldownExtraFilters.set(extraFilters);

    this.currentDashBoard.update((prev: any) => {
      if (!prev?.dashboard) return prev;
      return {
        ...prev,
        dashboard: {
          ...prev.dashboard,
          filters,
        },
      };
    });

    dashboardCache.clear();
    this.refreshWidgets.next();
  }

  private applyBaselineFilters(): void {
    if (!this.preDrilldownFilterSnapshot?.length) return;
    const filters = JSON.parse(JSON.stringify(this.preDrilldownFilterSnapshot));
    this.applyFilterState(filters, {});
  }

  private restorePreDrilldownFilters(): void {
    this.hydrateDrilldownTabContextFromStorage();
    this.applyBaselineFilters();
    this.clearDrilldownTabContext();
  }

  refreshWidgets= new Subject<void>()
  switchDashboard$ = new Subject<string>();


  /**
   * Update dashboard filters from external source (e.g., entity filters)
   * This enables bidirectional filter synchronization
   */
  updateDashboardFiltersFromExternal(externalFilters: any[]): void {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        console.warn('[OrgDashboardService] No dashboard context available for external filter update');
        return;
      }

      // Check if we have valid dashboard context or if we can still update filters
      // Allow updates even if not in dashboard context, as long as we have dashboard data
      const hasValidDashboard = currentDashboard && 
                                currentDashboard.dashboard && 
                                currentDashboard.dashboard.filters && 
                                Array.isArray(currentDashboard.dashboard.filters);

      if (!hasValidDashboard) {
        console.warn('[OrgDashboardService] No valid dashboard data available for external filter update');
        return;
      }

      // Merge external filters with existing dashboard filters
      const existingFilters = currentDashboard.dashboard.filters;
      const mergedFilters = this.mergeExternalFiltersWithDashboard(existingFilters, externalFilters);
      
      // Update dashboard state
      this.currentDashBoard.update((prev: any) => {
        if (prev) {
          return {
            ...prev,
            dashboard: {
              ...prev.dashboard,
              filters: mergedFilters
            }
          };
        }
        return prev;
      });

      // Update internal filter state
      this.updateFilterState(mergedFilters);
      
      // Store the updated state
      const dashboardKey = `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did}`;
      this.storeFilterState(dashboardKey, mergedFilters);
      
      // Trigger widget refresh only if we're actually in dashboard context
      if (this.isInDashboardContext()) {
        setTimeout(() => {
          this.refreshWidgets.next();
        }, 100);
      }
      
     // console.log('[OrgDashboardService] Updated dashboard filters from external source:', externalFilters);
    } catch (error) {
      console.error('[OrgDashboardService] Error updating dashboard filters from external source:', error);
    }
  }

  /**
   * Merge external filters with existing dashboard filters
   */
  private mergeExternalFiltersWithDashboard(existingFilters: any[], externalFilters: any[]): any[] {
    const result = [...existingFilters];
    //console.log(`[OrgDashboardService] Merging external filters:`, externalFilters);
    //console.log(`[OrgDashboardService] With existing filters:`, existingFilters.map(f => ({ name: f.name, key: f.key, default: f.default })));
    
    for (const externalFilter of externalFilters) {
      //console.log(`[OrgDashboardService] Processing external filter:`, { name: externalFilter.name, key: externalFilter.key, default: externalFilter.default });
      
      const existingIndex = result.findIndex((f: any) => {
        // Prioritize name matching over key matching
        if (externalFilter.name && f.name === externalFilter.name) {
          return true;
        }
        if (externalFilter.key && f.key === externalFilter.key) {
          return true;
        }
        return false;
      });
      
      //console.log(`[OrgDashboardService] Found existing filter at index:`, existingIndex);
      
      if (existingIndex >= 0) {
        // Update existing filter
        //console.log(`[OrgDashboardService] Updating existing filter at index ${existingIndex}:`, result[existingIndex]);
        result[existingIndex] = {
          ...result[existingIndex],
          ...externalFilter
        };
        //console.log(`[OrgDashboardService] Updated filter:`, result[existingIndex]);
      } else {
        // Add new filter if it has a valid schema
        if (externalFilter.name || externalFilter.key) {
          //console.log(`[OrgDashboardService] Adding new filter:`, externalFilter);
          result.push(externalFilter);
        } else {
          //console.log(`[OrgDashboardService] Skipping external filter - no name or key`);
        }
      }

      
    }
    
    //console.log(`[OrgDashboardService] Final merged result:`, result.map(f => ({ name: f.name, key: f.key, default: f.default })));
    return result;
  }

  /**
   * Check if a filter exists in the current dashboard
   */
  hasFilter(filterName: string): boolean {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        return false;
      }
      
      return currentDashboard.dashboard.filters.some((f: any) => 
        f.name === filterName || f.key === filterName
      );
    } catch (error) {
      console.error('[OrgDashboardService] Error checking filter existence:', error);
      return false;
    }
  }

  /**
   * Get filter by name from current dashboard
   */
  getFilterByName(filterName: string): any {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard?.dashboard?.filters) {
        return null;
      }
      
      return currentDashboard.dashboard.filters.find((f: any) => 
        f.name === filterName || f.key === filterName
      );
    } catch (error) {
      console.error('[OrgDashboardService] Error getting filter by name:', error);
      return null;
    }
  }

  /**
   * Check if there's valid dashboard data available for filter operations
   * This is useful when not in dashboard context but need to sync filters
   */
  hasValidDashboardData(): boolean {
    try {
      const currentDashboard = this.currentDashBoard();
      return !!(currentDashboard && 
                currentDashboard.dashboard && 
                currentDashboard.dashboard.filters && 
                Array.isArray(currentDashboard.dashboard.filters));
    } catch (error) {
      console.error('[OrgDashboardService] Error checking dashboard data validity:', error);
      return false;
    }
  }

  /**
   * Get dashboard key for current dashboard
   */
  getCurrentDashboardKey(): string | null {
    try {
      const currentDashboard = this.currentDashBoard();
      if (!currentDashboard) {
        return null;
      }
      return `${currentDashboard.org_id}_${currentDashboard.process_id}_${currentDashboard.org_process_did}`;
    } catch (error) {
      console.error('[OrgDashboardService] Error getting dashboard key:', error);
      return null;
    }
  }

  /**
   * Store entity filters that will be applied when dashboard loads
   */
  storePendingEntityFilters(dashboardKey: string, filters: any[]): void {
    try {
      this.pendingEntityFilters.set(dashboardKey, filters);
      //console.log('[OrgDashboardService] Stored pending entity filters for dashboard:', dashboardKey, filters);
      //console.log('[OrgDashboardService] All pending filter keys:', Array.from(this.pendingEntityFilters.keys()));
    } catch (error) {
      console.error('[OrgDashboardService] Error storing pending entity filters:', error);
    }
  }

  /**
   * Get pending entity filters for a dashboard
   */
  getPendingEntityFilters(dashboardKey: string): any[] {
    try {
      return this.pendingEntityFilters.get(dashboardKey) || [];
    } catch (error) {
      console.error('[OrgDashboardService] Error getting pending entity filters:', error);
      return [];
    }
  }

  /**
   * Clear pending entity filters for a dashboard
   */
  clearPendingEntityFilters(dashboardKey: string): void {
    try {
      this.pendingEntityFilters.delete(dashboardKey);
      //console.log('[OrgDashboardService] Cleared pending entity filters for dashboard:', dashboardKey);
    } catch (error) {
      console.error('[OrgDashboardService] Error clearing pending entity filters:', error);
    }
  }

  /**
   * Apply pending entity filters when dashboard loads
   */
  applyPendingEntityFilters(dashboardKey: string): void {
    try {
      //console.log('[OrgDashboardService] Checking for pending filters for dashboard key:', dashboardKey);
     // console.log('[OrgDashboardService] All available pending filter keys:', Array.from(this.pendingEntityFilters.keys()));
      const pendingFilters = this.getPendingEntityFilters(dashboardKey);
      //console.log('[OrgDashboardService] Found pending filters:', pendingFilters);
      
      if (pendingFilters.length > 0) {
       // console.log('[OrgDashboardService] Applying pending entity filters:', pendingFilters);
        this.updateDashboardFiltersFromExternal(pendingFilters);
        this.clearPendingEntityFilters(dashboardKey);
       // console.log('[OrgDashboardService] Successfully applied and cleared pending filters');
      } else {
       // console.log('[OrgDashboardService] No pending filters found for dashboard key:', dashboardKey);
        // Try to find similar keys
        const similarKeys = Array.from(this.pendingEntityFilters.keys()).filter(key => 
          key.includes(dashboardKey.split('_')[0]) && key.includes(dashboardKey.split('_')[1])
        );
        if (similarKeys.length > 0) {
         // console.log('[OrgDashboardService] Found similar keys that might match:', similarKeys);
        }
      }
    } catch (error) {
      console.error('[OrgDashboardService] Error applying pending entity filters:', error);
    }
  }

  export_excel(
    excel_styles: any,
    pivot_config: any,
    columns: any,
    query_name: string,
    exclude_columns: string[] = []
  ) {
    var body: any = {
      excel_styles,
      pivot_config,
      columns,
      filter: this.getFilterJsonForApiCall(),
      org_id: this.currentDashBoard()?.org_id, 
      process_id: this.currentDashBoard()?.process_id, 
    }
    if (Array.isArray(exclude_columns) && exclude_columns.length > 0) {
      body.exclude_columns = exclude_columns;
    }
    return this.http.post(`${environment.base_url}/store/processo/myquery/execute/${query_name}/excel`, body, {
      responseType: 'blob',
      observe: 'response'
    }).pipe(
      map(response => {
        const contentType = response.headers.get('content-type');
        
        if (contentType && (contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') || 
                           contentType.includes('application/vnd.ms-excel'))) {
          // Create blob and trigger download
          const blob = new Blob([response.body!], { type: contentType });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${query_name}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
        
        return response;
      })
    );
  }

  /**
   * Navigate from any widget to another dashboard + optional tab.
   * Stores filter overrides + tab in sessionStorage, then tells
   * EntityDashboardComponent to load the target dashboard via switchDashboard$.
   * EntityDashboardComponent runs its normal fetchDashboard() flow which
   * picks up the overrides.
   *
   * Supported drill_down_data keys:
   *   dashboard_id | dashboard  — target dashboard rd_id
   *   tab_id                    — pre-selects this tab on arrival
   *   params                    — { targetFilterName: sourceFilterName } mapping
   *
   * @param rowData  Row-level data from the clicked grid row (for params
   *                 whose source is a column value rather than a dashboard filter)
   */
  /**
   * Converts API-ready filter JSON (e.g. { from_dt, to_dt }) into dashboard
   * filter overrides and any remaining extra keys for drilldownExtraFilters.
   */
  convertApiFilterToDashboardState(
    apiFilter: Record<string, any>,
    dashboardFilters: any[] = []
  ): {
    overrides: Array<{ name: string; default: any }>;
    extra: Record<string, any>;
  } {
    const overrides: Array<{ name: string; default: any }> = [];
    const consumedKeys = new Set<string>();

    for (const f of dashboardFilters) {
      if (f.type === 'date_range') {
        const fromKey = `from_${f.name}`;
        const toKey = `to_${f.name}`;
        if (fromKey in apiFilter || toKey in apiFilter) {
          overrides.push({
            name: f.name,
            default: [
              apiFilter[fromKey] ? new Date(apiFilter[fromKey]) : null,
              apiFilter[toKey] ? new Date(apiFilter[toKey]) : null,
            ],
          });
          consumedKeys.add(fromKey);
          consumedKeys.add(toKey);
        }
      } else if (f.type === 'date' && f.name in apiFilter) {
        overrides.push({ name: f.name, default: new Date(apiFilter[f.name]) });
        consumedKeys.add(f.name);
      } else if (f.type === 'dropdown_multi_select' && f.name in apiFilter) {
        const val = apiFilter[f.name];
        overrides.push({
          name: f.name,
          default: val?.$in ?? (Array.isArray(val) ? val : [val]),
        });
        consumedKeys.add(f.name);
      } else if (f.name in apiFilter) {
        overrides.push({ name: f.name, default: apiFilter[f.name] });
        consumedKeys.add(f.name);
      }
    }

    const extra: Record<string, any> = {};
    Object.entries(apiFilter).forEach(([key, value]) => {
      if (!consumedKeys.has(key)) {
        extra[key] = value;
      }
    });

    return { overrides, extra };
  }

  performDashboardDrilldown(
    drill_down_data: any,
    sourceFilters: any[] = [],
    rowData: Record<string, any> = {}
  ): void {
    const targetDashboardId =
      drill_down_data?.dashboard_id || drill_down_data?.dashboard;
    if (!targetDashboardId) return;

    // 1. Store target tab so fetchDashboard's tab-restore logic picks it up
    const targetTabId = drill_down_data.tab_id;
    this.captureDrilldownTabContext(sourceFilters, targetTabId);
    if (targetTabId) {
      try {
        sessionStorage.setItem(
          `selected_tab_rd_${targetDashboardId}`,
          targetTabId
        );
      } catch {}
    }

    // 2. Build filter overrides from the params mapping.
    //    Format: { sourceName: targetFilterName }
    //    e.g. { "dt": "dt", "sq": "sq", "squad": "sq" }
    //      → "squad": "sq" means take `squad` from grid row, send as `sq` filter on target
    //    Lookup order: dashboard filters first, then rowData fallback
    //    Pivot aggregate labels ("Grand Total", "Subtotal - X") are not real values —
    //    "Grand Total" cells are skipped so prior drill state (e.g. Tab 1 → Tab 2)
    //    isn't clobbered; "Subtotal - X" is stripped to X to match the underlying row.
    const isGrandTotal = (v: any): boolean =>
      typeof v === 'string' && v.trim() === 'Grand Total';
    const normalizeRowValue = (v: any): any =>
      typeof v === 'string' && v.startsWith('Subtotal - ')
        ? v.replace('Subtotal - ', '')
        : v;

    const capturedFilters: Array<{ name: string; default: any }> = [];
    const capturedNames = new Set<string>();
    const hasMeaningfulValue = (value: any): boolean =>
      !(
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
      );

    if (drill_down_data.params) {
      for (const [sourceName, targetName] of Object.entries(
        drill_down_data.params as Record<string, string>
      )) {
        const rowValue = rowData ? rowData[sourceName] : undefined;
        if (hasMeaningfulValue(rowValue) && !isGrandTotal(rowValue)) {
          // Prefer clicked row value for explicit drill params (important for date pivot cells).
          capturedFilters.push({
            name: targetName,
            default: normalizeRowValue(rowValue),
          });
          capturedNames.add(targetName);
          continue;
        }

        const srcFilter = (sourceFilters as any[]).find(
          (f: any) => f.name === sourceName
        );
        if (srcFilter && hasMeaningfulValue(srcFilter.default)) {
          capturedFilters.push({
            name: targetName,
            default: JSON.parse(JSON.stringify(srcFilter.default)),
          });
          capturedNames.add(targetName);
        }
      }
    }
    // Also include any dashboard filter that has a value and isn't already captured.
    // This ensures user-selected filters (e.g. pn) are passed when drilling even if
    // not explicitly listed in params.
    (sourceFilters as any[]).forEach((f: any) => {
      if (!f?.name || capturedNames.has(f.name)) return;
      const hasValue =
        f.default !== null &&
        f.default !== undefined &&
        f.default !== '' &&
        !(Array.isArray(f.default) && f.default.length === 0);
      if (hasValue) {
        capturedFilters.push({
          name: f.name,
          default: JSON.parse(JSON.stringify(f.default)),
        });
        capturedNames.add(f.name);
      }
    });

    // Preserve prior drilldown overrides that didn't match a configured dashboard
    // filter on the source dashboard — those live in drilldownExtraFilters (see
    // entity-dashboard.component lines 291-298). Without this, a second-hop drill
    // (e.g. Tab 1 → Tab 2 → Tab 4) loses context set by the first hop: when the
    // user clicks a "Grand Total" cell in Tab 2 those row values are skipped, and
    // sourceFilters only contains configured dashboard filters — so call_type /
    // call_status etc. would be dropped entirely without this merge.
    const extraFilters = this.drilldownExtraFilters();
    if (extraFilters) {
      Object.entries(extraFilters).forEach(([name, value]) => {
        if (!name || capturedNames.has(name)) return;
        if (
          value === null ||
          value === undefined ||
          value === '' ||
          (Array.isArray(value) && value.length === 0)
        )
          return;
        capturedFilters.push({
          name,
          default: typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value,
        });
        capturedNames.add(name);
      });
    }

    if (capturedFilters.length > 0) {
      try {
        sessionStorage.setItem(
          'drilldown_filter_overrides',
          JSON.stringify(capturedFilters)
        );
      } catch {}
    }

    //console.log('[performDashboardDrilldown] emitting switchDashboard$:', targetDashboardId);
    this.switchDashboard$.next(targetDashboardId);
  }
}